/**
 * @fileoverview Header component.
 * @summary Application header with navigation and user menu.
 *
 * @package KaraoNow
 * @module apps/front/src/components/Layout/Header
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '../../hooks/redux.js'
import LanguageSelector from '../LanguageSelector.js'
import ThemeToggle from '../ThemeToggle.js'
import OrganizerMenu from '../../pages/organizer/components/OrganizerMenu.js'
import SingerMenu from '../../pages/singer/components/SingerMenu.js'
import PWAInstallButton from '../PWAInstallButton.js'
import PlanBadge from '../../pages/organizer/components/PlanBadge.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faQuestion } from '@fortawesome/free-solid-svg-icons'
import { getActivePlan } from '../../utils/planUtils.js'
import { SubscriptionType } from '@karaonow/shared'

/**
 * @summary Header component.
 * @returns {React.ReactElement} Header component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)

  // Pages where we never show the back button
  const noBackButtonPages = ['/', '/login', '/register', '/dashboard', '/singer/dashboard']
  
  // Check if we can safely go back without leaving the app
  // - Must not be on a "no back" page
  // - Must have history (window.history.length > 1 means there's a previous page)
  // - location.key !== 'default' means we've navigated within the app (React Router adds keys on navigation)
  const canGoBack = 
    !noBackButtonPages.includes(location.pathname) &&
    window.history.length > 1 &&
    location.key !== 'default'

  const isOrganizer = user?.role === 'organizer' || user?.role === 'superadmin'
  
  // Calculate active plan (only for organizers)
  const activePlan = isOrganizer ? getActivePlan(user) : SubscriptionType.FREE

  return (
    <header className="bg-(--color-bg-primary) shadow-md sticky top-0 z-50 header-container transition-colors">
      <div className="w-full header-inner">
        <div className="flex justify-between items-center py-3 header-content">
          {/* Back button and Logo - with proper spacing */}
          <div className="flex items-center shrink-0 header-logo-wrapper gap-1">
            {canGoBack && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                aria-label="Retour"
              >
                <FontAwesomeIcon icon={faAngleLeft} className="w-5 h-5" />
              </button>
            )}
            <h1>
              <div className="relative inline-block">
                <Link
                  to="/"
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold hover:opacity-80 transition-opacity text-gradient header-logo"
                >
                  KaraoNow
                </Link>
                {/* Plan badge (only for organizers) */}
                {isOrganizer && (
                  <PlanBadge 
                    planType={activePlan}
                    className="-top-2 -right-3"
                    onClick={() => navigate('/subscription')}
                  />
                )}
              </div>
            </h1>
          </div>

          {/* Navigation - responsive with proper spacing */}
          <nav className="flex items-center shrink-0 header-nav gap-1">
            {!isAuthenticated && <button className="flex items-center gap-1 text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors" 
            onClick={() => navigate('/about')}>
              <FontAwesomeIcon icon={faQuestion} aria-label="Aide" className="w-5 h-5" /> </button>}
            {/* PWA install button */}
            <PWAInstallButton variant="ghost" showLabel={false} />
            {/* Theme toggle */}
            <ThemeToggle />
            {/* Language selector */}
            <LanguageSelector />

            {isAuthenticated ? (
              <>
                {isOrganizer ? (
                  <OrganizerMenu />
                ) : (
                  <SingerMenu />
                )}
              </>
            ) : (
              /* SingerMenu handles guest mode internally */
              <SingerMenu />
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}
